import gql from 'graphql-tag';
import React, { MutableRefObject } from 'react';
import { MutationFn } from 'react-apollo';
import {
  CartApplePayHookFields,
  CoreCheckoutStartApplePayCheckout,
  CoreCheckoutUpdateApplePayShippingEstimate,
  core_apimessages_CheckoutPaymentMethod_Type as CheckoutPaymentMethodType,
  core_apimessages_Money as Money,
  core_apimessages_CheckoutFinalizeDigitalWalletPaymentRequest_ResultConfirmation as ResultConfirmation,
} from '../schemas/rql';
import ApplePay from '@adyen/adyen-web/dist/types/components/ApplePay';
import { adyenEnvironment } from '../lib/adyenEnvironment';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import AdyenCheckout from '@adyen/adyen-web';
import { isApplePayAvailable } from './applePayAvailability';
import {
  amountOwedLineItem,
  buildApplePayLineItems,
  CheckoutApplePayLineItemFragment,
  countryCodeOrDefault,
  FinalizePaymentMutationFn,
  findMerchantId,
  findRequiredShippingContactFields,
  guestUserEntry,
  mapJsonToBase64,
  merchantName,
  paymentContactToAddressEntry,
  withErrorHandling,
} from './checkoutApplePay';
import Location from '../lib/wrapped_location';
import { IUser } from '@reverbdotcom/commons';

export type StartCheckoutMutationFn =
  MutationFn<CoreCheckoutStartApplePayCheckout.Mutation, CoreCheckoutStartApplePayCheckout.Variables>;

export type UpdateShippingEstimateMutationFn =
  MutationFn<CoreCheckoutUpdateApplePayShippingEstimate.Mutation, CoreCheckoutUpdateApplePayShippingEstimate.Variables>;


type Checkout = CartApplePayHookFields.Fragment;

type CheckoutRef = MutableRefObject<Checkout>;

interface ApplePayMutations {
  startApplePayCheckout: StartCheckoutMutationFn;
  updateCheckoutShippingEstimate: UpdateShippingEstimateMutationFn;
  finalizeApplePayPayment: FinalizePaymentMutationFn;
}

/**
 * Powers Apple Pay for cases where a checkout hasn't been started yet,
 * which makes this hook suitable for use in cart, on item pages,
 * or anywhere where a currency and subtotal are known.
 */
export function useCartApplePay({
  checkoutBundlingId,
  paymentMethodsConfig,
  amountTotal,
  mutations,
}: {
  checkoutBundlingId: string;
  paymentMethodsConfig: string;
  amountTotal: Money;
  mutations: ApplePayMutations;
}) {
  const checkoutRef = React.useRef<Checkout>(null);
  const [applePay, setApplePay] = React.useState<ApplePay>(null);
  const [isLoading, setLoading] = React.useState(true);
  const { loggedOut, countryCode } = useUser();
  const { amountCents, currency } = amountTotal;
  const {
    startApplePayCheckout,
    updateCheckoutShippingEstimate,
    finalizeApplePayPayment,
  } = mutations;

  React.useEffect(() => {
    initializeApplePay({
      checkoutRef,
      checkoutBundlingId,
      paymentMethodsConfig,
      amountTotal: { amountCents, currency },
      user: { loggedOut, countryCode },
      mutations: {
        startApplePayCheckout,
        updateCheckoutShippingEstimate,
        finalizeApplePayPayment,
      },
      onApplePayAvailable: setApplePay,
      setLoading,
    });
  }, [
    countryCode,
    loggedOut,
    amountCents,
    currency,
    paymentMethodsConfig,
    checkoutBundlingId,
    startApplePayCheckout,
    updateCheckoutShippingEstimate,
    finalizeApplePayPayment,
    setApplePay,
    setLoading,
  ]);

  return { applePay, isLoading };
}

async function initializeApplePay({
  checkoutRef,
  checkoutBundlingId,
  paymentMethodsConfig,
  amountTotal,
  user,
  mutations,
  onApplePayAvailable,
  setLoading,
}: {
  checkoutRef: CheckoutRef,
  checkoutBundlingId: string,
  paymentMethodsConfig: string,
  amountTotal: Pick<Money, 'amountCents' | 'currency'>,
  user: Pick<IUser, 'countryCode' | 'loggedOut'>,
  mutations: ApplePayMutations,
  onApplePayAvailable: (applePay: ApplePay) => any,
  setLoading: (loading: boolean) => any,
}) {
  const applePayClient = buildApplePayClient({
    checkoutRef,
    checkoutBundlingId,
    mutations,
  });

  const paymentMethodsResponse = JSON.parse(paymentMethodsConfig);
  const merchantId = findMerchantId(paymentMethodsResponse);

  const adyenCheckout = new AdyenCheckout({
    paymentMethodsResponse,
    clientKey: adyenEnvironment.clientKey,
    environment: adyenEnvironment.environment,
  });

  const applePay = adyenCheckout.create<'applepay'>('applepay', {
    onShippingContactSelected: applePayClient.onShippingContactSelected,
    onAuthorized: applePayClient.onAuthorized,
    totalPriceStatus: 'pending',
    countryCode: countryCodeOrDefault(user.countryCode),
    amount: {
      value: amountTotal.amountCents,
      currency: amountTotal.currency,
    },
    requiredShippingContactFields: findRequiredShippingContactFields(user),
    configuration: {
      merchantId,
      merchantName: merchantName(),
    },
  });

  if (await isApplePayAvailable({ applePay, userCountryCode: user.countryCode })) {
    onApplePayAvailable(applePay);
  }
  setLoading(false);
}

export function buildApplePayClient({
  checkoutRef,
  checkoutBundlingId,
  mutations,
}: {
  checkoutRef: CheckoutRef;
  checkoutBundlingId: string;
  mutations: ApplePayMutations;
}) {
  function updateCheckoutRef(checkout: Checkout) {
    checkoutRef.current = checkout;
    return checkoutRef.current;
  }

  async function findOrCreateCheckout() {
    if (checkoutRef.current) {
      return checkoutRef.current;
    }

    const result = await withErrorHandling(() =>
      mutations.startApplePayCheckout({
        variables: {
          input: {
            checkoutBundlingId,
            paymentMethodType: CheckoutPaymentMethodType.APPLE_PAY,
          },
        },
      }),
    );

    if (!result || !result.data) {
      return null;
    }

    return updateCheckoutRef(result.data.startCheckout.checkout);
  }

  const onShippingContactSelected = buildShippingContactSelected(
    findOrCreateCheckout,
    mutations.updateCheckoutShippingEstimate,
    updateCheckoutRef,
  );

  const onAuthorized = buildPaymentAuthorized(
    checkoutRef,
    mutations.finalizeApplePayPayment,
  );

  return {
    onShippingContactSelected,
    onAuthorized,
  };
}

function buildShippingContactSelected(
  findOrCreateCheckout: () => Promise<Checkout>,
  updateCheckoutShippingEstimate: UpdateShippingEstimateMutationFn,
  updateCheckoutRef: (checkout: Checkout) => Checkout,
) {
  return async function onShippingContactSelected(
    resolve: (update: ApplePayJS.ApplePayShippingContactUpdate) => void,
    _reject: (update: ApplePayJS.ApplePayShippingContactUpdate) => void,
    event: ApplePayJS.ApplePayShippingContactSelectedEvent,
  ) {
    const checkout = await findOrCreateCheckout();

    const { locality, postalCode, administrativeArea, countryCode } = event.shippingContact;

    const result = await withErrorHandling(() =>
      updateCheckoutShippingEstimate({
        variables: {
          input: {
            id: checkout.id,
            shippingAddress: {
              locality,
              postalCode,
              region: administrativeArea,
              countryCode,
            },
          },
        },
      }),
    );

    if (!result || !result.data) {
      return;
    }

    const nextCheckout = updateCheckoutRef(result.data.updateCheckoutShippingEstimate.checkout);
    const { newTotal, newLineItems } = buildApplePayLineItems(nextCheckout);

    resolve({ newTotal, newLineItems });
  };
}

function buildPaymentAuthorized(
  checkoutRef: CheckoutRef,
  finalizeApplePayPayment: FinalizePaymentMutationFn,
) {
  return async function onAuthorized(
    resolve: () => void,
    reject: () => void,
    event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
  ) {
    const { payment: { shippingContact, token } } = event;
    const checkout = checkoutRef.current;
    const { amount: totalPrice } = checkout.lineItems.find(amountOwedLineItem);

    const result = await withErrorHandling(() =>
      finalizeApplePayPayment({
        variables: {
          input: {
            id: checkout.id,
            guestUser: guestUserEntry({ checkoutType: checkout.type, shippingContact }),
            totalPrice: {
              amountCents: totalPrice.amountCents,
              currency: totalPrice.currency,
            },
            paymentToken: mapJsonToBase64(token.paymentData),
            shippingAddress: paymentContactToAddressEntry(shippingContact),
            resultConfirmation: ResultConfirmation.SYNC,
          },
        },
      }));

    if (!result || !result.data) {
      reject();
      return;
    }

    const finalizedCheckout = result.data.finalizeDigitalWalletPayment.checkout;

    resolve();
    Location.assign(finalizedCheckout.action.redirectUrl);
  };
}

export const CartApplePayHookFragment = gql`
  fragment CartApplePayHookFields on Checkout {
    _id
    id
    type
    lineItems {
      type
      label
      amount {
        amount
        amountCents
        currency
      }
    }
    ...CheckoutApplePayLineItemFields
  }
  ${CheckoutApplePayLineItemFragment}
`;

export const START_APPLE_PAY_CHECKOUT = gql`
  mutation Core_Checkout_StartApplePayCheckout(
    $input: Input_core_apimessages_StartCheckoutRequest
  ) {
    startCheckout(input: $input) {
      checkout {
        _id
        ...CartApplePayHookFields
      }
    }
  }
  ${CartApplePayHookFragment}
`;

export const UPDATE_APPLE_PAY_SHIPPING_ESTIMATE = gql`
  mutation Core_Checkout_UpdateApplePayShippingEstimate(
    $input: Input_core_apimessages_CheckoutUpdateShippingEstimateRequest
  ) {
    updateCheckoutShippingEstimate(input: $input) {
      checkout {
        _id
        ...CartApplePayHookFields
      }
    }
  }
  ${CartApplePayHookFragment}
`;
